import React, { useRef, useState, useEffect } from "react";
import { cn, sanitizeFileName } from "@/lib/utils";
import { Upload, X, XCircle, Car, User2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";

interface IncomeProtectionViewProps {
  className?: string;
  illustrationUrl?: string;
  isEditMode?: boolean;
  onIllustrationChange?: (url: string | null) => void;
  formValues?: Record<string, any>;
  onFieldChange?: (fieldId: string, value: any) => void;
}

const INSURANCE_TYPES = ["BU", "UV", "PV", "HV", "GV"];

const WARNING_POINTS = [
  "Jede/r 5te berufsunfähig",
  "830.000 Unfälle pro Jahr",
  "Reformen und Umlageverfahren",
  "Ablebensschutz Partner/Familie",
];

export function IncomeProtectionView({
  className,
  illustrationUrl,
  isEditMode = false,
  onIllustrationChange,
  formValues = {},
  onFieldChange,
}: IncomeProtectionViewProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localImage, setLocalImage] = useState<string | null>(illustrationUrl || null);
  const [imageScale, setImageScale] = useState(100);
  const [isUploading, setIsUploading] = useState(false);

  // Form values
  const nettoEinkommen = formValues.income_netto || "";
  const gehaelter = formValues.income_gehaelter || "";
  const jahre = formValues.income_jahre || "";
  const arbeitskraftWert = formValues.income_arbeitskraft_wert || "";
  const monatlichWert = formValues.income_monatlich_wert || "";

  useEffect(() => {
    setLocalImage(illustrationUrl || null);
  }, [illustrationUrl]);

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const safeName = sanitizeFileName(file.name);
      const filePath = `income-protection-illustrations/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("beratung-documents")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("beratung-documents")
        .getPublicUrl(filePath);

      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error("Konnte keine öffentliche URL erzeugen");

      setLocalImage(publicUrl);
      onIllustrationChange?.(publicUrl);
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setLocalImage(null);
    onIllustrationChange?.(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className={cn("p-6 space-y-6", className)}>
      {/* Title section */}
      <div className="space-y-1">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Einkommen </span>
          <span className="text-primary">sichern</span>
        </h2>
        <p className="text-lg italic text-muted-foreground">
          Sicherheit für Ihr regelmäßiges Einkommen.
        </p>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left: Info panel */}
        <div className="col-span-12 lg:col-span-7 space-y-6">
          {/* Insurance types */}
          <div className="bg-primary text-primary-foreground rounded-lg p-3 text-center">
            <span className="font-bold text-lg">
              {INSURANCE_TYPES.join(" | ")}
            </span>
          </div>

          {/* -50% Warning */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-red-500">
              <XCircle className="h-8 w-8" />
              <span className="text-3xl font-bold">-50% Einbußen</span>
            </div>

            <div className="space-y-2">
              {WARNING_POINTS.map((point, index) => (
                <div key={index} className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <span>{point}</span>
                </div>
              ))}
            </div>

            <p className="font-semibold mt-4">
              Können Sie sich eine Halbierung Ihres Einkommens leisten?
            </p>
          </div>

          {/* Calculation */}
          <div className="border-l-4 border-primary pl-4 space-y-4">
            <p className="text-sm">
              Nettoeinkommen x Gehälter x Jahre bis zur Alterspension = <strong>Wert der Arbeitskraft</strong>
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={nettoEinkommen}
                onChange={(e) => onFieldChange?.("income_netto", e.target.value)}
                className="w-20 h-8 text-center"
                placeholder=""
              />
              <span>x</span>
              <Input
                value={gehaelter}
                onChange={(e) => onFieldChange?.("income_gehaelter", e.target.value)}
                className="w-16 h-8 text-center"
                placeholder=""
              />
              <span>x</span>
              <Input
                value={jahre}
                onChange={(e) => onFieldChange?.("income_jahre", e.target.value)}
                className="w-16 h-8 text-center"
                placeholder=""
              />
              <span>= €</span>
              <Input
                value={arbeitskraftWert}
                onChange={(e) => onFieldChange?.("income_arbeitskraft_wert", e.target.value)}
                className="w-28 h-8 text-right"
                placeholder=""
              />
              <span>,-</span>
            </div>

            <div className="flex items-center justify-between gap-4 pt-2">
              <span className="text-sm font-medium">
                Wie viel ist Ihnen Ihre Arbeitskraft monatlich wert?
              </span>
              <div className="flex items-center gap-2">
                <span>€</span>
                <Input
                  value={monatlichWert}
                  onChange={(e) => onFieldChange?.("income_monatlich_wert", e.target.value)}
                  className="w-28 h-8 text-right"
                  placeholder=""
                />
                <span>,-</span>
              </div>
            </div>
          </div>

          {/* Icons */}
          <div className="flex justify-center gap-8 pt-4">
            <User2 className="h-12 w-12 text-primary" />
            <Car className="h-12 w-12 text-primary" />
          </div>
        </div>

        {/* Right: Image */}
        <div className="col-span-12 lg:col-span-5">
          <div className="border-2 border-primary rounded-lg p-4 h-full flex flex-col items-center justify-center min-h-[350px]">
            {localImage ? (
              <div className="relative w-full h-full flex flex-col items-center justify-center gap-2">
                <img
                  src={localImage}
                  alt="Einkommen sichern Illustration"
                  className="object-contain transition-transform"
                  style={{
                    maxWidth: `${imageScale}%`,
                    maxHeight: `${Math.min(320, 320 * imageScale / 100)}px`
                  }}
                />
                {isEditMode && (
                  <>
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-0 right-0 h-8 w-8"
                      onClick={handleRemoveImage}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <div className="w-full max-w-[180px] mt-2">
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        Größe: {imageScale}%
                      </Label>
                      <Slider
                        value={[imageScale]}
                        onValueChange={([v]) => setImageScale(v)}
                        min={50}
                        max={300}
                        step={10}
                        className="w-full"
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center p-4 space-y-4 w-full h-full">
                {isEditMode ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                      }}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      disabled={isUploading}
                      className="border-dashed border-2 h-32 w-full flex flex-col gap-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {isUploading ? "Upload läuft..." : "Grafik hochladen"}
                      </span>
                    </Button>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Upload className="h-8 w-8" />
                    <span className="text-sm">Bild wird hier angezeigt</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
